import React, { useState, useEffect } from 'react';
import './FighterIssueCounter.css';
import CountryFlag from 'react-country-flag';
import FighterEvolution from './FighterEvolution';
import { getCountryName } from './countryUtils';

function FighterIssueCounter({ darkMode }) {
    const [fighters, setFighters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Actual state (used for API calls and data display)
    const [gender, setGender] = useState('men');
    const [rankThreshold, setRankThreshold] = useState(1);
    const [rankType, setRankType] = useState('division'); // 'global' or 'division'
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedWeightClass, setSelectedWeightClass] = useState('');
    
    // Pending/staged state (updated by controls, applied on button click)
    const [pendingGender, setPendingGender] = useState('men');
    const [pendingRankThreshold, setPendingRankThreshold] = useState(1);
    const [pendingRankType, setPendingRankType] = useState('division');
    const [pendingSelectedCountry, setPendingSelectedCountry] = useState('');
    const [pendingSelectedWeightClass, setPendingSelectedWeightClass] = useState('');
    
    const [sortConfig, setSortConfig] = useState({ key: 'issueCount', direction: 'desc' });
    const [latestIssue, setLatestIssue] = useState(null);

    // Convert division name (e.g., 'Heavyweight') back to division ID for API
    const getDivisionId = (divisionName) => {
        const divisionMap = {
            'Heavyweight': 1, 'Light Heavyweight': 2, 'Middleweight': 3, 'Welterweight': 4, 'Lightweight': 5, 'Featherweight': 6, 'Bantamweight': 7, 'Flyweight': 8,
            'Women\'s Strawweight': 13, 'Women\'s Flyweight': 14, 'Women\'s Bantamweight': 15
        };
        return divisionMap[divisionName] || null;
    };

    // Generate all valid tick values based on the pattern
    function getValidTickValues() {
        const ticks = [];
        // 1-20: step by 1
        for (let i = 1; i <= 20; i++) ticks.push(i);
        // 25-50: step by 5
        for (let i = 25; i <= 50; i += 5) ticks.push(i);
        // 60-200: step by 10
        for (let i = 60; i <= 200; i += 10) ticks.push(i);
        // 300-1000: step by 100
        for (let i = 300; i <= 1000; i += 100) ticks.push(i);
        return ticks;
    }

    // Convert threshold value to slider position (0-100)
    function thresholdToSlider(thresholdValue) {
        const ticks = getValidTickValues();
        const index = ticks.indexOf(thresholdValue);
        if (index === -1) return 0;
        return (index / (ticks.length - 1)) * 100;
    }

    // Convert slider position (0-100) to threshold value
    function sliderToThreshold(sliderValue) {
        const ticks = getValidTickValues();
        const index = Math.round((sliderValue / 100) * (ticks.length - 1));
        return ticks[Math.max(0, Math.min(index, ticks.length - 1))];
    }

    // Helper function to convert issue number to quarter date
    // Uses a fixed reference point: Issue 1 = January 1, 1990
    // Each issue is 3 months apart, so Issue N = Jan 1, 1990 + (N-1)*3 months
    function getIssueDate(issueNum) {
        const issue1Date = new Date(1990, 0, 1); // January 1, 1990 - Issue 1
        const monthsFromIssue1 = (issueNum - 1) * 3;
        
        const date = new Date(issue1Date);
        date.setMonth(date.getMonth() + monthsFromIssue1);
        
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    }

    // Fetch latest issue number on component mount
    useEffect(() => {
        const fetchLatestIssue = async () => {
            try {
                const apiBase = process.env.REACT_APP_API_URL || '';
                const res = await fetch(`${apiBase}/api/latest-issue`);
                const data = await res.json();
                if (res.ok && data.latestIssue) {
                    setLatestIssue(data.latestIssue);
                }
            } catch (e) {
                console.error('Failed to fetch latest issue:', e);
            }
        };
        fetchLatestIssue();
    }, []);

    // Fetch fighters with their issue counts on component mount only
    useEffect(() => {
        fetchFighters();
    }, []);

    const fetchFighters = async () => {
        setLoading(true);
        setError('');
        try {
            const apiBase = process.env.REACT_APP_API_URL || '';
            let url = `${apiBase}/api/fighter-issue-count?gender=${gender}&rank_threshold=${rankThreshold}&rank_type=${rankType}`;
            if (selectedWeightClass) {
                const divisionId = getDivisionId(selectedWeightClass);
                if (divisionId) {
                    url += `&division=${divisionId}`;
                }
            }
            const res = await fetch(url);
            const data = await res.json();
            if (res.ok && Array.isArray(data)) {
                setFighters(data);
            } else {
                setError(data.error || 'Failed to fetch fighters');
            }
        } catch (e) {
            setError('Server error');
            console.error(e);
        }
        setLoading(false);
    };

    // Apply pending changes and fetch data
    const handleApply = () => {
        setGender(pendingGender);
        setRankThreshold(pendingRankThreshold);
        setRankType(pendingRankType);
        setSelectedCountry(pendingSelectedCountry);
        setSelectedWeightClass(pendingSelectedWeightClass);
        
        // Fetch with the pending values (they'll be committed immediately due to closure)
        setLoading(true);
        setError('');
        
        (async () => {
            try {
                const apiBase = process.env.REACT_APP_API_URL || '';
                let url = `${apiBase}/api/fighter-issue-count?gender=${pendingGender}&rank_threshold=${pendingRankThreshold}&rank_type=${pendingRankType}`;
                if (pendingSelectedWeightClass) {
                    const divisionId = getDivisionId(pendingSelectedWeightClass);
                    if (divisionId) {
                        url += `&division=${divisionId}`;
                    }
                }
                const res = await fetch(url);
                const data = await res.json();
                if (res.ok && Array.isArray(data)) {
                    setFighters(data);
                } else {
                    setError(data.error || 'Failed to fetch fighters');
                }
            } catch (e) {
                setError('Server error');
                console.error(e);
            }
            setLoading(false);
        })();
    };

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'desc' };
        });
    };

    const getSortIcon = (key) => {
        const activeColor = '#888';
        const isActive = sortConfig.key === key;
        const direction = isActive ? sortConfig.direction : null;
        
        if (!isActive) return null;
        
        return (
            <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6, fontSize: '0.75em', color: activeColor }}>
                {direction === 'asc' ? '▲' : '▼'}
            </span>
        );
    };

    const getSortedFighters = () => {
        let filtered = fighters;
        
        // Apply country filter
        if (selectedCountry) {
            filtered = filtered.filter(fighter => 
                fighter.country_code && fighter.country_code.toUpperCase() === selectedCountry.toUpperCase()
            );
        }
        
        // Apply weight class filter
        if (selectedWeightClass) {
            filtered = filtered.filter(fighter => 
                fighter.division && fighter.division === selectedWeightClass
            );
        }
        
        if (!sortConfig.key) return filtered;
        
        const sorted = [...filtered].sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];
            
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
            }
            
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        
        return sorted;
    };

    const getUniqueCountries = () => {
        const countries = [...new Set(fighters
            .filter(f => f.country_code)
            .map(f => f.country_code.toUpperCase())
        )].sort();
        return countries;
    };

    const getUniqueWeightClasses = () => {
        const weightOrder = {
            'Heavyweight': 1,
            'Light Heavyweight': 2,
            'Middleweight': 3,
            'Welterweight': 4,
            'Lightweight': 5,
            'Featherweight': 6,
            'Bantamweight': 7,
            'Flyweight': 8,
            'Women\'s Strawweight': 9,
            'Women\'s Flyweight': 10,
            'Women\'s Bantamweight': 11
        };
        
        const weightClasses = [...new Set(fighters
            .filter(f => f.division)
            .map(f => f.division)
        )].sort((a, b) => (weightOrder[a] || 999) - (weightOrder[b] || 999));
        return weightClasses;
    };

    return (
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2em' }}>
            {/* Page Header */}
            <div style={{ marginBottom: '2em' }}>
                <h1 style={{ margin: '0 0 0.5em 0', fontSize: '2.5em', fontWeight: 500 }}>
                    Longitudinal Individual Rankings
                </h1>
                <p style={{ margin: 0, color: '#aaa', fontSize: '1.1em' }}>
                    Number of quarterly appearances with rank ≤ N (pound-for-pound or in weight class).
                    <br />
                    <br />
                    Global ranks ≤ 15 are directly sourced from Fightmatrix's quarterly P4P top 15 lists.
                    <br />
                    Global ranks &gt; 15 are estimated from applying customized coefficients to weight class-specific Fightmatrix rankings.
                </p>
            </div>

            {/* Main Container - Controls and Table Side by Side */}
            <div style={{ display: 'flex', gap: '2em', alignItems: 'flex-start' }}>
                {/* Controls Sidebar */}
                <div style={{ flex: '0 0 auto', minWidth: '280px', marginBottom: '2em', display: 'flex', flexDirection: 'column', gap: '1.5em', background: '#1a1a1a', padding: '12px', borderRadius: '4px', border: '1px solid #555' }}>
                    {/* Gender Toggle - Top Centered */}
                    <div 
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexDirection: 'column',
                            gap: '0.5em'
                        }}
                    >
                        <div 
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.35rem 0.5rem',
                                cursor: 'pointer',
                                userSelect: 'none',
                                transition: 'all 0.25s ease'
                            }}
                            onClick={() => setPendingGender(pendingGender === 'men' ? 'women' : 'men')}
                        >
                            <span style={{ fontSize: '0.9rem', color: '#ddd', fontWeight: 500, minWidth: '40px', textAlign: 'center' }}>Men</span>
                            <div style={{
                                width: '32px',
                                height: '20px',
                                background: '#444',
                                borderRadius: '10px',
                                position: 'relative',
                                transition: 'all 0.25s ease',
                                border: '1px solid #555'
                            }}>
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    background: '#fff',
                                    borderRadius: '50%',
                                    position: 'absolute',
                                    top: '2px',
                                    left: pendingGender === 'men' ? '2px' : '14px',
                                    transition: 'all 0.25s ease'
                                }} />
                            </div>
                            <span style={{ fontSize: '0.9rem', color: '#ddd', fontWeight: 500, minWidth: '40px', textAlign: 'center' }}>Women</span>
                        </div>
                        <span style={{ fontSize: '0.75rem', color: '#999', fontWeight: 400, textAlign: 'center' }}>(for global rank only)</span>
                    </div>

                    {/* Rank Type Toggle - Above Slider */}
                    <div 
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                        onClick={() => setPendingRankType(pendingRankType === 'global' ? 'division' : 'global')}
                    >
                        <div 
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.35rem 0.5rem',
                                cursor: 'pointer',
                                userSelect: 'none',
                                transition: 'all 0.25s ease'
                            }}
                        >
                            <span style={{ fontSize: '0.9rem', color: '#ddd', fontWeight: 500, minWidth: '70px', textAlign: 'center' }}>Global Rank</span>
                            <div style={{
                                width: '32px',
                                height: '20px',
                                background: '#444',
                                borderRadius: '10px',
                                position: 'relative',
                                transition: 'all 0.25s ease',
                                border: '1px solid #555'
                            }}>
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    background: '#fff',
                                    borderRadius: '50%',
                                    position: 'absolute',
                                    top: '2px',
                                    left: pendingRankType === 'global' ? '2px' : '14px',
                                    transition: 'all 0.25s ease'
                                }} />
                            </div>
                            <span style={{ fontSize: '0.9rem', color: '#ddd', fontWeight: 500, minWidth: '70px', textAlign: 'center' }}>Division Rank</span>
                        </div>
                    </div>

                    {/* Rank Threshold Input */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5em', flex: 1 }}>
                        <div>
                            <label style={{ color: '#ddd', fontWeight: 500, display: 'block', marginBottom: '0.5em' }}>
                                Threshold: <b>{pendingRankThreshold}</b>
                            </label>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                step="0.1"
                                value={thresholdToSlider(pendingRankThreshold)}
                                onChange={(e) => setPendingRankThreshold(sliderToThreshold(Number(e.target.value)))}
                                style={{
                                    width: '100%',
                                    accentColor: '#888',
                                    height: '4px',
                                    marginTop: '8px'
                                }}
                            />
                        </div>

                        {/* Country Filter */}
                        <div>
                            <label style={{ color: '#ddd', fontWeight: 500, display: 'block', marginBottom: '0.5em' }}>Country</label>
                            <select
                                value={pendingSelectedCountry}
                                onChange={(e) => setPendingSelectedCountry(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '0.5em',
                                    background: '#1a1a1a',
                                    color: '#ddd',
                                    border: '1px solid #555',
                                    borderRadius: '4px',
                                    fontSize: '0.9em',
                                    cursor: 'pointer',
                                    fontWeight: 500
                                }}
                            >
                                <option value="" style={{ background: '#1a1a1a' }}>All Countries</option>
                                {getUniqueCountries().map((country) => (
                                    <option key={country} value={country} style={{ background: '#1a1a1a' }}>
                                        {getCountryName(country)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Weight Class Filter */}
                        <div>
                            <label style={{ color: '#ddd', fontWeight: 500, display: 'block', marginBottom: '0.5em' }}>Weight Class</label>
                            <select
                                value={pendingSelectedWeightClass}
                                onChange={(e) => setPendingSelectedWeightClass(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '0.5em',
                                    background: '#1a1a1a',
                                    color: '#ddd',
                                    border: '1px solid #555',
                                    borderRadius: '4px',
                                    fontSize: '0.9em',
                                    cursor: 'pointer',
                                    fontWeight: 500
                                }}
                            >
                                <option value="" style={{ background: '#1a1a1a' }}>All Weight Classes</option>
                                {getUniqueWeightClasses().map((weightClass) => (
                                    <option key={weightClass} value={weightClass} style={{ background: '#1a1a1a' }}>
                                        {weightClass}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Apply Button at Bottom */}
                    <button
                        onClick={handleApply}
                        style={{
                            height: 40,
                            padding: '8px 16px',
                            fontSize: '1em',
                            fontWeight: 400,
                            borderRadius: 6,
                            border: '1px solid #888',
                            background: '#232323',
                            color: '#ddd',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            width: 'auto',
                            whiteSpace: 'nowrap',
                            alignSelf: 'center'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = '#1a1a1a';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = '#232323';
                        }}
                    >
                        Apply
                    </button>
                </div>

                {/* Table Container */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    {error && <div style={{ color: '#ff6b6b', marginBottom: '1em', fontSize: '1.1em' }}>{error}</div>}

                    {loading ? (
                        <div style={{ color: '#aaa', textAlign: 'center', padding: '2em' }}>Loading...</div>
                    ) : fighters.length > 0 ? (
                        <div style={{ height: '600px', overflowY: 'auto', border: '1px solid #555', borderRadius: '4px', scrollbarColor: '#444 #1a1a1a', scrollbarWidth: 'thin' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                                <tr style={{ background: darkMode ? '#222' : '#f5f5f5', color: darkMode ? '#f1f1f1' : '#222' }}>
                                    <th style={{ padding: '0.35em', textAlign: 'center', fontWeight: 400, borderBottom: '1px solid #555', width: '50px' }}>#</th>
                                    <th style={{ padding: '0.35em', textAlign: 'left', fontWeight: 400, borderBottom: '1px solid #555' }}>Name</th>
                                    <th style={{ padding: '0.35em', textAlign: 'center', fontWeight: 400, borderBottom: '1px solid #555' }}>
                                        <button
                                            onClick={() => handleSort('issueCount')}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: darkMode ? '#f1f1f1' : '#222',
                                                fontWeight: 400,
                                                cursor: 'pointer',
                                                padding: 0,
                                                fontSize: '1em',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                            }}
                                            aria-label="Sort by issue count"
                                        >
                                            Quarters
                                            {getSortIcon('issueCount')}
                                        </button>
                                    </th>
                                    <th style={{ padding: '0.35em', textAlign: 'center', fontWeight: 400, borderBottom: '1px solid #555' }}>
                                        <button
                                            onClick={() => handleSort('bestRank')}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: darkMode ? '#f1f1f1' : '#222',
                                                fontWeight: 400,
                                                cursor: 'pointer',
                                                padding: 0,
                                                fontSize: '1em',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                            }}
                                            aria-label="Sort by peak rank"
                                        >
                                            Peak Rank
                                            {getSortIcon('bestRank')}
                                        </button>
                                    </th>
                                    <th style={{ padding: '0.35em', textAlign: 'center', fontWeight: 400, borderBottom: '1px solid #555' }}>
                                        <button
                                            onClick={() => handleSort('lastIssue')}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: darkMode ? '#f1f1f1' : '#222',
                                                fontWeight: 400,
                                                cursor: 'pointer',
                                                padding: 0,
                                                fontSize: '1em',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                            }}
                                            aria-label="Sort by first appearance"
                                        >
                                            First
                                            {getSortIcon('lastIssue')}
                                        </button>
                                    </th>
                                    <th style={{ padding: '0.35em', textAlign: 'center', fontWeight: 400, borderBottom: '1px solid #555' }}>
                                        <button
                                            onClick={() => handleSort('firstIssue')}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: darkMode ? '#f1f1f1' : '#222',
                                                fontWeight: 400,
                                                cursor: 'pointer',
                                                padding: 0,
                                                fontSize: '1em',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                            }}
                                            aria-label="Sort by last appearance"
                                        >
                                            Last
                                            {getSortIcon('firstIssue')}
                                        </button>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {getSortedFighters().map((fighter, idx) => {
                                    const allFighters = getSortedFighters();
                                    const prevFighter = idx > 0 ? allFighters[idx - 1] : null;
                                    const showRank = !prevFighter || prevFighter.issueCount !== fighter.issueCount;
                                    const countryCode = fighter.country_code ? fighter.country_code.toUpperCase() : null;
                                    // Convert UK to GB and RU-* to RU for react-country-flag
                                    const flagCode = countryCode === 'UK' ? 'GB' : (countryCode && countryCode.startsWith('RU-') ? 'RU' : countryCode);

                                    return (
                                        <tr
                                            key={fighter.name + fighter.profile_link}
                                            style={{
                                                borderBottom: '1px solid #444',
                                                background: idx % 2 === 0 ? (darkMode ? '#1a1a1a' : '#f9f9f9') : (darkMode ? '#1a1a1a' : '#f5f5f5'),
                                                transition: 'background 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = darkMode ? '#252525' : '#efefef';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = idx % 2 === 0 ? (darkMode ? '#1a1a1a' : '#f9f9f9') : (darkMode ? '#1a1a1a' : '#f5f5f5');
                                            }}
                                        >
                                            <td style={{ padding: '0.35em', color: '#ddd', textAlign: 'center', fontWeight: 400 }}>
                                                {showRank ? idx + 1 : ''}
                                            </td>
                                            <td style={{ padding: '0.35em', color: '#ddd' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                                                    {flagCode && (
                                                        <CountryFlag countryCode={flagCode} svg style={{ width: '1.2em', height: '1.2em' }} />
                                                    )}
                                                    {fighter.profile_link ? (
                                                        <a
                                                            href={`https://www.fightmatrix.com${fighter.profile_link}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                color: '#6ba3ff',
                                                                textDecoration: 'none',
                                                                transition: 'color 0.2s ease'
                                                            }}
                                                            onMouseEnter={(e) => e.target.style.color = '#8bb5ff'}
                                                            onMouseLeave={(e) => e.target.style.color = '#6ba3ff'}
                                                        >
                                                            {fighter.name}
                                                        </a>
                                                    ) : (
                                                        fighter.name
                                                    )}
                                                </div>
                                            </td>
                                            <td style={{ padding: '0.35em', textAlign: 'center', color: '#ddd', fontSize: '1.1em', fontWeight: 400 }}>
                                                {fighter.issueCount}
                                            </td>
                                            <td style={{ padding: '0.35em', textAlign: 'center', color: '#ddd', fontSize: '1em', fontWeight: 400 }}>
                                                {fighter.bestRank}
                                            </td>
                                            <td style={{ padding: '0.35em', textAlign: 'center', color: '#aaa', fontSize: '0.9em' }}>
                                                {getIssueDate(fighter.lastIssue)}
                                            </td>
                                            <td style={{ padding: '0.35em', textAlign: 'center', color: '#aaa', fontSize: '0.9em' }}>
                                                {getIssueDate(fighter.firstIssue)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        </div>
                    ) : (
                        <div style={{ color: '#aaa', textAlign: 'center', padding: '2em', fontSize: '1.1em' }}>
                            No fighters found with global rank ≤ {rankThreshold}.
                        </div>
                    )}
                </div>
            </div>

            {/* Fighter Evolution Chart */}
            <div style={{ marginTop: '4em' }}>
                <FighterEvolution gender={gender} />
            </div>
        </div>
    );
}

export default FighterIssueCounter;
